// src/documents/closingData.js
//
// Valores del Closing Statement (formato de Adjustify, ejemplo recibido
// 2026-09-23). El Closing va al CLIENTE y se hace por cada cheque, después
// de que el PA lo deposita y define el breakdown (flujo del cliente,
// Fase 4). El formulario tiene exactamente los renglones del documento;
// cada uno puede llevar monto o quedar vacío:
//   Total Check Amount − Public Adjuster Fee − (Mitigation, Tarp, Mold Test,
//   Remediation, Mediation, Appraiser, Umpire, Attorney) = Net to Client
//
// Honorario del PA: sugerido como % del cheque truncado al centavo (ejemplo
// real: 3,299.39 × 15% = 494.9085 → $494.90, neto $2,804.49); se puede
// corregir a mano.
import { insuredNames } from './spolData';

export const PAYER_PRINTED = 'Luis Claudio Lic-W541409';

// Renglones "(If Applicable)" del documento, en su orden.
export const CLOSING_FEES = [
  { key: 'mitigationFee', label: 'Mitigation Fee(If Applicable):', es: 'Mitigación' },
  { key: 'tarpFee', label: 'Tarp Fee(If Applicable):', es: 'Tarp' },
  { key: 'moldTestFee', label: 'Mold Test Fee(If Applicable):', es: 'Prueba de moho' },
  { key: 'remediationFee', label: 'Remediation Fee(If Applicable):', es: 'Remediación' },
  { key: 'mediationFee', label: 'Mediation Fee(If Applicable):', es: 'Mediación' },
  { key: 'appraiserFee', label: 'Appraiser Fee(If Applicable):', es: 'Appraiser' },
  { key: 'umpireFee', label: 'Umpire Fee(If Applicable):', es: 'Umpire' },
  { key: 'attorneyFee', label: 'Attorney Fee(If Applicable):', es: 'Abogado' }
];

// Texto que va en "as a Coverage ___" (el ejemplo dice "Final Payment").
export const PAYMENT_TYPES = [
  { value: 'Initial Payment', es: 'Pago inicial' },
  { value: 'Partial Payment', es: 'Pago parcial' },
  { value: 'Supplemental Payment', es: 'Pago suplementario' },
  { value: 'Final Payment', es: 'Pago final' },
  { value: 'Recoverable Depreciation', es: 'Depreciación recuperable (RCV)' }
];

const num = v => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isNaN(n) ? null : n;
};
const toCents = v => Math.round(v * 100);
const fromCents = c => c / 100;

export const money = v => (num(v) === null ? '' : `$${num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const plainMoney = v => (num(v) === null ? '' : num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Honorario del PA sugerido: % del cheque truncado al centavo.
export const suggestPaFee = (checkAmount, pct) => (num(checkAmount) === null || num(pct) === null ? null : fromCents(Math.floor(toCents(num(checkAmount)) * num(pct) / 100)));

// closing = { checkAmount, feePercent, paFee?, mitigationFee, tarpFee, …, attorneyFee, paymentType, date }
export const computeBreakdown = (closing = {}) => {
  const checkAmount = num(closing.checkAmount);
  const paFee = num(closing.paFee) ?? suggestPaFee(checkAmount, closing.feePercent);
  const feesCents = CLOSING_FEES.reduce((sum, fee) => sum + toCents(num(closing[fee.key]) || 0), 0);
  const netToClient = checkAmount === null ? null : fromCents(toCents(checkAmount) - toCents(paFee || 0) - feesCents);
  return { checkAmount, paFee, otherFees: fromCents(feesCents), netToClient };
};

const usDate = iso => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : '';
};

export const buildClosingData = (claimRecord = {}, closing = {}, language = 'es') => {
  const breakdown = computeBreakdown(closing);
  const insureds = insuredNames(claimRecord);
  const values = {
    insureds,
    claimNumber: claimRecord.claimNumber || '',
    policyNumber: claimRecord.policyNumber || '',
    insuranceCompany: claimRecord.insuranceCompanyName || '',
    checkAmount: money(breakdown.checkAmount),
    feePercent: num(closing.feePercent) === null ? '' : `${num(closing.feePercent)}%`,
    paFee: money(breakdown.paFee),
    // Renglón vacío = no aplica (queda en blanco, igual que el documento original).
    ...Object.fromEntries(CLOSING_FEES.map(fee => [fee.key, num(closing[fee.key]) ? money(closing[fee.key]) : ''])),
    netToClient: money(breakdown.netToClient),
    netPlain: plainMoney(breakdown.netToClient),
    paymentType: closing.paymentType || '',
    date: usDate(closing.date),
    payer: PAYER_PRINTED
  };

  const es = language === 'es';
  const missing = [
    !insureds && (es ? 'Asegurado' : 'Insured'),
    !values.claimNumber && (es ? 'Número de reclamo' : 'Claim number'),
    !values.policyNumber && (es ? 'Número de póliza' : 'Policy number'),
    !values.insuranceCompany && (es ? 'Aseguradora' : 'Insurance company'),
    breakdown.checkAmount === null && (es ? 'Monto del cheque' : 'Check amount'),
    breakdown.paFee === null && (es ? 'Honorario del PA' : 'PA fee'),
    !values.paymentType && (es ? 'Tipo de pago' : 'Payment type'),
    !values.date && (es ? 'Fecha' : 'Date')
  ].filter(Boolean);

  const errors = breakdown.netToClient !== null && breakdown.netToClient < 0
    ? [es ? 'Los costos superan el monto del cheque (neto negativo).' : 'Fees exceed the check amount (negative net).']
    : [];

  return { values, missing, errors, breakdown };
};
